import React from 'react';
import isEmpty from 'lodash/isEmpty';
import { CandlestickSeries } from 'react-stockcharts/lib/series';

import { ForeCastType } from 'shared/interface/enums';
import { IChartDetails, IChartSettingsType } from '../interface/futureTable';
import GroupedBarSeries from '../util/GroupedBarSeries';
import RenderIndicatorsOnchart from './renderIndicatorsOnchart';
import { RenderChart } from './stockChart';
import { getColorForDataType } from 'shared/util/utility';

interface IProps {
	chartType: string;
	chartDetails: IChartDetails;
	chartSettings: IChartSettingsType[];
	ele: IChartSettingsType;
	getBarChartYAccsesor: any;
	cardIndex: number;
	materialIndex?: number;
	setBarWidth?: (width: { [key: string]: number }) => void;
	strokeColor: any;
	colorList: any;
	data?: any;
	forecastKeys?: string[];
	selectedKeys?: string[]; // <-- Add this prop for selected value keys (l, h, e, etc.)
	barSeriesStartIndexMap?: Record<string, number>;
}

const CommonChart: React.FC<IProps> = (props) => {
	const {
		chartSettings,
		getBarChartYAccsesor,
		chartType,
		ele,
		chartDetails,
		cardIndex,
		materialIndex,
		setBarWidth,
		strokeColor,
		colorList,
		data,
		forecastKeys,
		barSeriesStartIndexMap
	} = props;

	const index = chartSettings.findIndex((chart) => `${chart.id}` === `${ele.id}`);
	const actualPriceData = data.filter(
		(item: any) => item[`cv${ele.symbol}-${ele.boerse}-${materialIndex || index}`]?.type === ForeCastType.ACTUAL_PRICE
	);
	const actualPriceLastData = actualPriceData[actualPriceData.length - 1];
	const lastActualPriceDate = !isEmpty(actualPriceData) ? actualPriceLastData?.date : new Date();
	const isForecastValue = chartDetails && chartDetails?.forecastAccess === 0;
	const selectedValueKeys = props.selectedKeys && props.selectedKeys.length > 0 ? props.selectedKeys : ['l'];

	return (
		<>
			{chartType === 'bar' && (
				<GroupedBarSeries
					id={chartDetails.symbol}
					yAccessor={getBarChartYAccsesor}
					fill={(d: any, i: any) => {
						// Compute this chart's local index range within the flattened yAccessor list
						const startIndex = (barSeriesStartIndexMap && barSeriesStartIndexMap[`${ele.id}`]) || 0;
						const selectedDataTypes = props.selectedKeys && props.selectedKeys.length > 0 ? props.selectedKeys : ['l'];
						const countForThisChart = selectedDataTypes.length;
						const endIndexExclusive = startIndex + countForThisChart;

						// If this bar index does not belong to this chart's group, keep default gradient
						if (i < startIndex || i >= endIndexExclusive) {
							return 'none';
						}

						const strokeArray = Array.isArray(ele.color) ? ele.color : [ele.color];

						// Derive the local index inside this chart's selected data types group
						const localIndex = i - startIndex;
						const seriesIdx = startIndex + localIndex;
						const seriesData = d[`cv${ele.symbol}-${ele.boerse}-${seriesIdx}`];

						if (!seriesData) return 'none';

						if (seriesData.type === ForeCastType.FORECASTED_PRICE && seriesData.source_data) {
							const forecastKeysSafe = forecastKeys || [];
							const availableSources = Object.keys(seriesData.source_data);
							let selectedSource = availableSources[0];
							if (forecastKeysSafe.length > 0 && availableSources.includes(forecastKeysSafe[0])) {
								selectedSource = forecastKeysSafe[0];
							}
							const sourceIndex = chartDetails?.sources?.indexOf(selectedSource) ?? 0;
							return `url(#MyGradientListBarForecast${sourceIndex})`;
						}
						const baseColor = strokeArray[0] || '#4472c4';
						const chartGroupingColor = getColorForDataType(baseColor, localIndex, selectedValueKeys || []);
						return chartGroupingColor;
						// return `url(#bar${chartSettings[i].id}-${cardIndex})`;
					}}
					stroke={strokeColor} // Stroke colors for the datasets
					setBarWidth={setBarWidth}
					className={(d: any) =>
						isForecastValue && d[`cv${ele.symbol}-${ele.boerse}-${materialIndex || index}`]?.type === ForeCastType.FORECASTED_PRICE
							? 'forecast-blur'
							: ''
					}
				/>
			)}

			{chartType === 'candle' && (
				<CandlestickSeries
					yAccessor={(d: any) => {
						return {
							close: d[`cv${ele.symbol}-${ele.boerse}-${materialIndex || index}`]?.l,
							open: d[`cv${ele.symbol}-${ele.boerse}-${materialIndex || index}`]?.e,
							high: d[`cv${ele.symbol}-${ele.boerse}-${materialIndex || index}`]?.h,
							low: d[`cv${ele.symbol}-${ele.boerse}-${materialIndex || index}`]?.t
						};
					}}
					fill={(d: any) => (d.close < d.open ? '#F53030' : '#40AD1A')}
					wickStroke={(d: any) => (d.close < d.open ? '#F53030' : '#40AD1A')}
					stroke={(d: any) => (d.close < d.open ? '#F53030' : '#40AD1A')}
					widthRatio={0.6}
					opacity={1}
				/>
			)}
			{!chartDetails.isIntraday && chartType !== 'bar' && (
				<>
					{selectedValueKeys.length > 1 ? (
						<RenderChart
							type={chartType}
							yAccessors={selectedValueKeys.map((key) => {
								// Compute global start index for this material
								const startIndex = (barSeriesStartIndexMap && barSeriesStartIndexMap[`${ele.id}`]) || 0;
								// Local index of the field within this material
								const localIdx = Array.isArray(ele.fields) ? Math.max(0, ele.fields.indexOf(key)) : 0;
								// Global series index in flattened API keys
								const seriesIdx = startIndex + localIdx;
								return (d: any) => {
									const seriesData = d[`cv${ele.symbol}-${ele.boerse}-${seriesIdx}`];
									if (seriesData?.type === ForeCastType.ACTUAL_PRICE || chartSettings.length > 1) {
										return seriesData?.l;
									}
								};
							})}
							fills={selectedValueKeys.map((key) => colorList[key] || ele.color)}
							stroke={selectedValueKeys.map((key) => colorList[key] || ele.color)}
						/>
					) : (
						<RenderChart
							type={chartType}
							yAccessor={(d: any) => {
								const key = selectedValueKeys[0] || 'l';
								const startIndex = (barSeriesStartIndexMap && barSeriesStartIndexMap[`${ele.id}`]) || 0;
								const localIdx = Array.isArray(ele.fields) ? Math.max(0, ele.fields.indexOf(key)) : 0;
								const seriesIdx = startIndex + localIdx;
								const seriesData = d[`cv${ele.symbol}-${ele.boerse}-${seriesIdx}`];
								if (seriesData?.type === ForeCastType.ACTUAL_PRICE || chartSettings.length > 1) {
									return seriesData?.l;
								}
							}}
							fill={`url(#MyGradient${ele.symbol}-${ele.boerse}${ele.id}-${cardIndex})`}
							stroke={ele.color}
						/>
					)}
					{chartSettings.length === 1 && (
						<>
							{chartDetails?.sources && (
								<RenderChart
									type={chartType}
									yAccessors={[
										...Object.keys(colorList).map((key) => (d: any) => {
											const data = d[`cv${ele.symbol}-${ele.boerse}-${materialIndex || index}`];
											const sourceData = data?.source_data;
											// Return the 'l' value for the key, only if the key exists in the source data
											if (data?.type === ForeCastType.FORECASTED_PRICE) {
												return sourceData?.[key]?.l;
											} else if (lastActualPriceDate === d?.date) {
												return actualPriceLastData[`cv${ele.symbol}-${ele.boerse}-${index}`]?.l;
											}
										})
									]}
									fills={[
										...Object.keys(colorList).map((key, idx) => {
											// Dynamically map fills to predefined gradient URLs
											return `url(#MyGradientListForecast${idx})`; // Assuming each gradient has an index (0, 1, 2)
										})
									]}
									stroke={strokeColor} // Stroke colors for the datasets
									chartDetails={chartDetails}
									selectedDataTypes={selectedValueKeys}
								/>
							)}
						</>
					)}
				</>
			)}
			{chartDetails.isIntraday && chartType !== 'bar' && (
				<RenderChart
					type={chartType}
					yAccessor={(d: any) => {
						// Intraday: use global start index + local field index
						const key = selectedValueKeys[0] || 'l';
						const startIndex = (barSeriesStartIndexMap && barSeriesStartIndexMap[`${ele.id}`]) || 0;
						const localIdx = Array.isArray(ele.fields) ? Math.max(0, ele.fields.indexOf(key)) : 0;
						const seriesIdx = startIndex + localIdx;
						return d[`cv${ele.symbol}-${ele.boerse}-${seriesIdx}`]?.l;
					}}
					fill={`url(#MyGradient${ele.symbol}-${ele.boerse}${ele.id}-${cardIndex})`}
					stroke={ele.color}
				/>
			)}
			{ele.indicators.map((indi, index) => {
				return (
					<RenderIndicatorsOnchart
						key={`${ele.id}-${indi.value}`}
						index={index}
						type={indi.value}
						color={ele.color}
						yAccessor={(d: any) => {
							if (d[`cv${ele.symbol}-${ele.boerse}-0`]?.type === 'actualPrice') {
								return d[`${indi.value}-${ele.symbol}-${ele.boerse}-${ele.id}`];
							} else {
								return null;
							}
						}}
					/>
				);
			})}
		</>
	);
};

export default CommonChart;
